import { Fragment } from "react";

import clsx from "clsx";

import { useChartState } from "@features/sales-v3/hooks";
import { FloatingDelayGroup } from "@floating-ui/react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { constants, type ITimeGranularityV2 } from "@north-beam/nb-common";
import { renderChevronAnimation } from "@shared/selects/utils";

import { CompareCustomDateRangeSelect } from "./compare-custom-date-range-select";
import { SamePeriodDifferentYearSubmenu } from "./same-period-different-year-submenu";

interface Props {
  className?: string;
  timeGranularity: ITimeGranularityV2;
  compareDateRange: constants.CompareDateRange | null;
  onCompareDateRangeUpdate(value: constants.CompareDateRange | null): void;
}

export const CompareDateRangeMenu = ({
  className,
  timeGranularity,
  compareDateRange,
  onCompareDateRangeUpdate,
}: Props) => {
  const { removeAllCompareDateRangeFromCharts } = useChartState();

  const clearCompareDateRange = () => {
    /* The chart inherits the compare date range from the page configuration so if there is none
       unset it on the charts */
    removeAllCompareDateRangeFromCharts();
    onCompareDateRangeUpdate(null);
  };
  const selectPreviousPeriod = () => {
    onCompareDateRangeUpdate({
      value: "previousPeriod",
    });
  };
  const selectLastPeriod = () => {
    onCompareDateRangeUpdate({
      value: "lastPeriod",
    });
  };
  const selectPreviousYear = () => {
    onCompareDateRangeUpdate({
      value: "samePeriodPriorYear",
    });
  };
  const selectSamePeriodDifferentYear = (
    calendar: "standard",
    year: number,
  ) => {
    onCompareDateRangeUpdate({
      value: "samePeriodDifferentYear",
      year,
      calendar,
    });
  };
  const selectCustomDateRange = ({
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  }) => {
    onCompareDateRangeUpdate({
      value: "custom",
      startDate,
      endDate,
    });
  };

  return (
    <Popover className="relative text-sm">
      <PopoverButton
        as="div"
        className={clsx("nb-input-container w-40", className)}
      >
        {({ open }) => (
          <div
            className={clsx(
              "nb-input flex justify-between items-center",
              "bg-white cursor-pointer h-inherit",
              { "border border-cosmic ring-1 ring-cosmic": open },
            )}
          >
            <div
              className={clsx(
                "text-ellipsis whitespace-nowrap overflow-hidden max-w-full",
                "flex-grow text-left",
              )}
            >
              {!compareDateRange && (
                <span className="text-disabled">Compare to...</span>
              )}
              {compareDateRange
                ? constants.CompareDateRangeTypes.getLabel(
                    compareDateRange.value,
                  )
                : ""}
            </div>
            <span
              className="pl-2 justify-self-end"
              onClick={(event) => {
                if (!open) {
                  event.preventDefault();
                  event.stopPropagation();
                }
                clearCompareDateRange();
              }}
            >
              {compareDateRange ? (
                <i
                  className={clsx(
                    "fa-regular fa-sharp fa-circle-xmark text-sm",
                    "group-hover:text-cosmic+1 text-cosmic",
                  )}
                />
              ) : (
                renderChevronAnimation(open)
              )}
            </span>
          </div>
        )}
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel className="absolute rounded-md bg-white elevation-1 pt-3 pb-2 left-0 w-56 z-10 mt-2">
          {({ close }) => (
            <>
              <div
                className={clsx(
                  "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold",
                  "px-4 py-1.5 flex items-center justify-between",
                )}
                onClick={() => {
                  selectPreviousPeriod();
                  close();
                }}
              >
                <span className="text-interstellar leading-loose">
                  Previous period
                </span>
              </div>
              {(timeGranularity === "weekly" ||
                timeGranularity === "monthly") && (
                <div
                  className={clsx(
                    "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold",
                    "px-4 py-1.5 flex items-center justify-between",
                  )}
                  onClick={() => {
                    selectLastPeriod();
                    close();
                  }}
                >
                  <span className="text-interstellar leading-loose">
                    Last period
                  </span>
                </div>
              )}
              <FloatingDelayGroup delay={{ open: 0, close: 2000 }}>
                {timeGranularity !== "hourly" && (
                  <>
                    <div
                      className={clsx(
                        "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold",
                        "px-4 py-1.5 flex items-center justify-between",
                      )}
                      onClick={() => {
                        selectPreviousYear();
                        close();
                      }}
                    >
                      <span className="text-interstellar leading-loose">
                        Same period, prior year
                      </span>
                    </div>
                    <SamePeriodDifferentYearSubmenu
                      selectSamePeriodDifferentYear={(calendar, year) => {
                        selectSamePeriodDifferentYear(calendar, year);
                        close();
                      }}
                      compareDateRange={compareDateRange}
                    />
                  </>
                )}
                <CompareCustomDateRangeSelect
                  placement="bottom"
                  timeGranularity={timeGranularity}
                  compareDateRange={compareDateRange}
                  selectCustomDateRange={(dateRange) => {
                    selectCustomDateRange(dateRange);
                    close();
                  }}
                />
              </FloatingDelayGroup>
            </>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};
